import { useEffect, useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import SearchableSelect from './SearchableSelect';
import {
  useAddPrize, useAdminChampions, useAdminPeople, useAdminPrizes, useDeletePrize,
} from '../../hooks/useQuestAdmin';
import {
  currentQuarter, fmtDate, periodLabel, recentQuarters,
} from './questShared';
import {
  BTN, BTN_SECONDARY, ErrorNote, INPUT, LABEL, Loading, PeriodSelect, Section,
} from './adminUi';

// Quarterly prizes (brief section 6): the quarter's four champions, worked
// out from the points — Option B, one prize per person. Each champion's row
// comes filled in: the suggested winner, the prize given for that award last
// time, today's date. Comms needn't give all four: they tick the awards they
// are giving this quarter — one, two, any — change a winner if they need to
// (the row says who it was changed from), and confirm, which records whoever
// is selected and tells them. Opens on the quarter that has just finished,
// since that is the one with prizes to give.

// [award key, title, what wins it]. The brief's names, except the two it also
// gives to badges (Quest Champion, Values Champion), renamed so a prize is
// never mistaken for a badge. The key is stored with each prize given, so the
// app plays that award's own moment and a rename never loses a past prize.
const CHAMPIONS = [
  ['quest', 'Points Champion', 'most points'],
  ['values', 'Values Ambassador', 'most values recognitions'],
  ['knowledge', 'Knowledge Champion', 'best average quiz score, two Quests or more'],
  ['culture', 'Culture Champion', 'most colleague recognitions'],
];
const awardOf = (title) => CHAMPIONS.find(([, t]) => t === title)?.[0];
// A prize given for this award: by its award, or by title for one recorded
// before awards were stored.
const isFor = (p, title) => (p.award ? p.award === awardOf(title) : p.title === title);

const today = () => new Date().toISOString().slice(0, 10);

// The quarter before this one — the latest whose champions are final.
export const lastFinishedQuarter = () => recentQuarters(2)[1];

// 1 while a quarter has champions but no prize recorded at all — a nudge that
// prizes are due. Once any is given, the rest are Comms' choice, so no nudge.
export function usePrizesToConfirm(quarter) {
  const champions = useAdminChampions(quarter);
  const prizes = useAdminPrizes();
  if (!champions.data || !prizes.data) return 0;
  if (prizes.data.some((p) => p.quarter === quarter)) return 0;
  return CHAMPIONS.some(([key]) => champions.data[key]) ? 1 : 0;
}

// Optional: a one-off prize that isn't one of the four champions (the brief's
// champions are "for example"), typed in from scratch.
function RecordPrize({ quarter, onDone, onCancel, people }) {
  const add = useAddPrize();
  const [title, setTitle] = useState('');
  const [who, setWho] = useState('');
  const [prize, setPrize] = useState('');
  const [awardedOn, setAwardedOn] = useState(today());

  const save = async (e) => {
    e.preventDefault();
    try {
      await add.mutateAsync({ quarter, title, entra_user_id: who, prize, awarded_on: awardedOn });
      onDone();
    } catch { /* shown below */ }
  };

  return (
    <form onSubmit={save} className="grid gap-3 border-t border-gray-100 bg-gray-50 p-4 sm:grid-cols-4">
      <div className="sm:col-span-4">
        <p className="text-sm font-semibold text-gray-900">A one-off prize</p>
        <p className="text-xs text-gray-500">
          Only for an award that isn't one of the four champions above — say a Safety Star. You don't need this to give the champion prizes.
        </p>
      </div>
      <label className="block">
        <span className={LABEL}>Prize for</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Safety Star" className={INPUT} />
      </label>
      <div>
        <span className={LABEL}>Winner</span>
        <SearchableSelect value={who} onChange={setWho} placeholder="Pick the winner" options={people} />
      </div>
      <label className="block">
        <span className={LABEL}>Prize</span>
        <input value={prize} onChange={(e) => setPrize(e.target.value)} placeholder="e.g. £50 voucher" className={INPUT} />
      </label>
      <label className="block">
        <span className={LABEL}>Date given</span>
        <input type="date" value={awardedOn} onChange={(e) => setAwardedOn(e.target.value)} className={INPUT} />
      </label>
      <div className="space-y-2 sm:col-span-4">
        <ErrorNote error={add.error} />
        <span className="flex gap-2">
          <button type="submit" disabled={!who || !title.trim() || add.isPending} className={BTN}>Record prize</button>
          <button type="button" onClick={onCancel} className={BTN_SECONDARY}>Cancel</button>
        </span>
      </div>
    </form>
  );
}

function PrizesTab() {
  const [quarter, setQuarter] = useState(lastFinishedQuarter());
  const champions = useAdminChampions(quarter);
  const prizes = useAdminPrizes();
  const peopleQ = useAdminPeople();
  const add = useAddPrize();
  const remove = useDeletePrize();
  const [other, setOther] = useState(false);
  const [drafts, setDrafts] = useState({}); // title -> { give, who, prize, on }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const people = (peopleQ.data || []).map((p) => ({ value: p.entra_user_id, label: p.display_name }));
  const nameOf = (id) => people.find((p) => p.value === id)?.label || 'someone';
  const given = (prizes.data || []).filter((p) => p.quarter === quarter);
  const givenFor = (title) => given.find((p) => isFor(p, title));
  const lastPrizeFor = (title) => (prizes.data || []).find((p) => isFor(p, title) && p.prize)?.prize || '';
  const running = quarter === currentQuarter();

  // Fill each champion's row in: the suggested winner and last time's prize.
  useEffect(() => {
    if (!champions.data || !prizes.data) return;
    setDrafts(Object.fromEntries(CHAMPIONS.map(([key, title]) => [title, {
      give: false,
      who: champions.data[key]?.entra_user_id || '',
      prize: lastPrizeFor(title),
      on: today(),
    }])));
  }, [quarter, champions.data, prizes.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDraft = (title, patch) => {
    setError(null);
    setDrafts((d) => ({ ...d, [title]: { ...d[title], ...patch } }));
  };
  // the awards ticked to give, and whether each has a winner picked
  const picked = CHAMPIONS.map(([, title]) => title).filter((title) => !givenFor(title) && drafts[title]?.give);
  const ready = picked.filter((title) => drafts[title]?.who);

  const confirm = async (titles) => {
    setBusy(true);
    setError(null);
    try {
      for (const title of titles) {
        const d = drafts[title];
        // eslint-disable-next-line no-await-in-loop
        await add.mutateAsync({
          quarter, title, award: awardOf(title), entra_user_id: d.who, prize: d.prize, awarded_on: d.on,
        });
      }
    } catch (e) { setError(e); }
    setBusy(false);
  };

  return (
    <div className="space-y-3">
      <PeriodSelect value={quarter} onChange={(v) => { setQuarter(v); setOther(false); }} withYears={false} />

      <Section defaultOpen title={`Champions for ${periodLabel(quarter)}`}
        description={running
          ? 'This quarter is still running, so the winners can still change.'
          : 'Filled in from the points. Tick the ones you are giving — as many or as few as you like — then confirm.'}
        badge={`${given.length} given`}
        action={ready.length > 1 ? (
          <button type="button" onClick={() => confirm(ready)} disabled={busy} className={`${BTN} px-3 py-1 text-xs`}>
            Confirm ticked ({ready.length})
          </button>
        ) : null}>
        <Loading q={champions} what="the champions" />
        {champions.data ? (
          <ul className="divide-y divide-gray-100">
            {CHAMPIONS.map(([key, title, measure]) => {
              const c = champions.data[key];
              const done = givenFor(title);
              const d = drafts[title] || {};
              const changed = c && d.who && d.who !== c.entra_user_id;
              return (
                <li key={key} className="grid items-center gap-3 px-4 py-3 md:grid-cols-[14rem_1fr_11rem_9rem_auto]">
                  {done ? (
                    <span>
                      <span className="block text-sm font-semibold text-gray-900">{title}</span>
                      <span className="block text-xs text-gray-400">{c ? `${c.display_name} · ${c.measure}` : measure}</span>
                    </span>
                  ) : (
                    <label className="flex cursor-pointer items-start gap-2.5">
                      <input type="checkbox" checked={Boolean(d.give)} onChange={(e) => setDraft(title, { give: e.target.checked })}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand" />
                      <span>
                        <span className="block text-sm font-semibold text-gray-900">{title}</span>
                        <span className="block text-xs text-gray-400">{c ? `${c.display_name} · ${c.measure}` : measure}</span>
                      </span>
                    </label>
                  )}
                  {done ? (
                    <span className="text-sm text-gray-700 md:col-span-4">
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">Given</span>
                      {' '}{done.display_name || 'Unknown'}{done.prize ? ` · ${done.prize}` : ''}{done.awarded_on ? ` · ${fmtDate(done.awarded_on)}` : ''}
                    </span>
                  ) : !d.give ? (
                    <span className="text-xs text-gray-400 md:col-span-4">Not giving this one — tick it to give it.</span>
                  ) : (
                    <>
                      <div className="min-w-0">
                        <SearchableSelect value={d.who || ''} onChange={(v) => setDraft(title, { who: v })}
                          placeholder={c ? 'Pick the winner' : 'Nobody qualifies — pick someone'} options={people} />
                        {changed ? <span className="mt-0.5 block text-[11px] text-amber-700">Changed from {c.display_name}</span> : null}
                      </div>
                      <input value={d.prize || ''} onChange={(e) => setDraft(title, { prize: e.target.value })}
                        placeholder="Prize, e.g. £50 voucher" className={INPUT} aria-label={`${title} prize`} />
                      <input type="date" value={d.on || today()} onChange={(e) => setDraft(title, { on: e.target.value })}
                        className={INPUT} aria-label={`${title} date given`} />
                      <button type="button" onClick={() => confirm([title])} disabled={!d.who || busy} className={`${BTN} px-3 py-2`}>
                        Confirm
                      </button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}
        {error ? <div className="px-4 pb-3"><ErrorNote error={error} /></div> : null}
        {other ? (
          <RecordPrize quarter={quarter} people={people} onDone={() => setOther(false)} onCancel={() => setOther(false)} />
        ) : (
          <button type="button" onClick={() => setOther(true)}
            className="w-full border-t border-gray-100 px-4 py-2.5 text-left text-xs font-semibold text-brand hover:text-brand-dark">
            + Give a one-off prize (optional — not one of the four champions)
          </button>
        )}
      </Section>

      <Section title="Prize history" description="Every prize recorded, newest first." badge={prizes.data ? prizes.data.length : null}>
        <Loading q={prizes} what="the prizes" />
        {prizes.data && !prizes.data.length ? <p className="px-4 py-6 text-center text-sm text-gray-400">No prizes recorded yet.</p> : null}
        <ul className="divide-y divide-gray-100">
          {(prizes.data || []).map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900">
                  {periodLabel(p.quarter)} · {p.title} — {p.display_name || nameOf(p.entra_user_id)}
                </span>
                <span className="block text-xs text-gray-400">
                  {[p.prize, p.awarded_on ? fmtDate(p.awarded_on) : null, p.recorded_by_name ? `recorded by ${p.recorded_by_name}` : null]
                    .filter(Boolean).join(' · ')}
                </span>
              </span>
              <button type="button" aria-label="Remove" disabled={remove.isPending}
                onClick={() => { if (window.confirm('Remove this prize from the record?')) remove.mutate({ id: p.id }); }}
                className="rounded p-1.5 text-gray-400 hover:bg-rose-50 hover:text-rose-600">
                <TrashIcon className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
        <div className="px-4 pb-3 empty:hidden"><ErrorNote error={remove.error} /></div>
      </Section>
    </div>
  );
}

export default PrizesTab;
